import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import TeamEventsChart from "../../components/charts/TeamEventsChart";
import Loader from "../../components/ui/Loader";
import EmptyState from "../../components/ui/EmptyState";
import { useApi } from "../../services/useApi";
import { useAuth } from "../auth/useAuth";
import { getTeamSessionDetails, getTeamSessions } from "./teamService";
import { formatDateTime, formatDuration } from "../../utils/date";
import {
  createSessionNote,
  deleteSessionNote,
  deleteSessionNoteImage,
  getSessionNotes,
  updateSessionNote,
  uploadSessionNoteImage,
} from "./sessionNotesService";
import NoteCard from "../../components/notes/NoteCard";

const TeamSessionDetailPage = () => {
  const { sessionId, teamId } = useParams();
  const api = useApi();
  const { token } = useAuth();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notesData, setNotesData] = useState(null);
  const [activeNoteUser, setActiveNoteUser] = useState(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!token || !sessionId) {
        setLoading(false);
        return;
      }
      try {
        const data = await getTeamSessionDetails(api, sessionId);
        if (!mounted) return;
        setDetails(data);
      } catch (err) {
        if (!mounted) return;
        setDetails(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [api, token, sessionId]);

  const handleUpdateNote = async (id, description) => {
    await updateSessionNote(api, id, description, false);
    setNotesData((prev) =>
      prev
        ? {
            ...prev,
            notes: prev.notes.map((n) =>
              n.id === id ? { ...n, description } : n,
            ),
          }
        : prev,
    );
  };

  const handleUploadImage = async (id, file) => {
    const uploaded = await uploadSessionNoteImage(api, id, file);
    setNotesData((prev) =>
      prev
        ? {
            ...prev,
            notes: prev.notes.map((n) =>
              n.id === id
                ? { ...n, files: [...(n.files || []), uploaded] }
                : n,
            ),
          }
        : prev,
    );
  };

  const handleDeleteNote = async (id) => {
    await deleteSessionNote(api, id);
    setNotesData((prev) =>
      prev ? { ...prev, notes: prev.notes.filter((n) => n.id !== id) } : prev,
    );
  };

  const handleDeleteImage = async (noteId, imageId, imageUrl) => {
    let prevState;
    setNotesData((prev) => {
      prevState = prev;
      return prev
        ? {
            ...prev,
            notes: prev.notes.map((n) =>
              n.id === noteId
                ? {
                    ...n,
                    files: (n.files || []).filter((f) =>
                      imageId
                        ? f.id !== imageId && f.imageId !== imageId
                        : f.url !== imageUrl,
                    ),
                  }
                : n,
            ),
          }
        : prev;
    });
    try {
      if (!imageId) return;
      await deleteSessionNoteImage(api, noteId, imageId);
    } catch {
      if (prevState) setNotesData(prevState);
    }
  };

  const handleAddNote = async () => {
    const created = await createSessionNote(api, sessionId, "TEAM", "", false);
    const newNote = {
      id: created?.id,
      description: "",
      user: notesData?.currentUserInformation?.userInfo || null,
      belongsToCurrentUser: true,
      createdAt: new Date().toISOString(),
      files: [],
      __autoEdit: true,
    };
    setNotesData((prev) =>
      prev
        ? { ...prev, notes: [newNote, ...(prev.notes || [])] }
        : { notes: [newNote] },
    );
    setActiveNoteUser(newNote.user?.publicId || null);
  };

  const openNotesModal = () => {
    setNotesData((prev) =>
      prev
        ? {
            ...prev,
            notes: (prev.notes || []).map((n) =>
              n.__autoEdit ? { ...n, __autoEdit: false } : n,
            ),
          }
        : prev,
    );
    setShowNotesModal(true);
  };

  useEffect(() => {
    let mounted = true;
    const loadSessionInfo = async () => {
      if (!token || !teamId) return;
      try {
        const data = await getTeamSessions(api, teamId);
        const found = (data?.content || []).find(
          (s) => s.sessionId === sessionId,
        );
        if (!mounted) return;
        setSessionInfo(found || null);
      } catch {
        if (!mounted) return;
        setSessionInfo(null);
      }
    };
    loadSessionInfo();
    return () => {
      mounted = false;
    };
  }, [api, token, teamId, sessionId]);

  const formatSessionTitle = () => {
    const rawTitle =
      sessionInfo?.title ||
      details?.title ||
      details?.session?.title ||
      details?.sessionTitle ||
      "";
    const title = String(rawTitle || "").trim();
    if (title) return title;
    const createdAt =
      sessionInfo?.createdAt || details?.createdAt || details?.session?.createdAt;
    if (!createdAt) return details?.place || details?.session?.place || "Session";
    const date = new Date(createdAt);
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "long" });
    const year = date.getFullYear();
    const place =
      sessionInfo?.place || details?.place || details?.session?.place;
    return `${day} ${month}, ${year}${place ? ` ${place}` : ""}`;
  };

  useEffect(() => {
    let mounted = true;
    const loadNotes = async () => {
      if (!token || !sessionId) return;
      try {
        const data = await getSessionNotes(api, sessionId, "TEAM");
        if (!mounted) return;
        setNotesData(data);
        const firstUserId = data?.notes?.[0]?.user?.publicId || null;
        setActiveNoteUser(firstUserId);
      } catch {
        if (!mounted) return;
        setNotesData(null);
      }
    };
    loadNotes();
    return () => {
      mounted = false;
    };
  }, [api, token, sessionId]);

  const teamName =
    sessionInfo?.teamName || details?.team?.name || details?.teamName || "Team";
  const teamPhoto =
    sessionInfo?.pictureUrl ||
    details?.team?.pictureUrl ||
    details?.pictureUrl ||
    "/icons/Logo_PNG.png";

  return (
    <div>
      <div className="detail-header">
        <div className="detail-header-title">{formatSessionTitle()}</div>
        <div className="detail-header-meta">
          <img src={teamPhoto} alt={teamName} />
          <span>{teamName}</span>
        </div>
      </div>
      {loading && <Loader />}
      {!loading && !details && (
        <EmptyState title="No data" message="Session details not found." />
      )}
      {!loading && details && (
        <div className="detail-stack">
          <div className="detail-split">
            <div className="card detail-meta">
              <div>Session Title: {formatSessionTitle()}</div>
              <div>
                Session Notes:{" "}
                {notesData?.notes ? notesData.notes.length : "—"}
              </div>
              <div>
                Place:{" "}
                {sessionInfo?.place ||
                  details?.place ||
                  details?.session?.place ||
                  "-"}
              </div>
              <div>
                Started:{" "}
                {formatDateTime(
                  sessionInfo?.createdAt ||
                    details?.createdAt ||
                    details?.session?.createdAt,
                )}
              </div>
              <div>
                Ended:{" "}
                {formatDateTime(
                  sessionInfo?.endedAt ||
                    details?.endedAt ||
                    details?.session?.endedAt,
                )}
              </div>
              <div>
                Duration:{" "}
                {formatDuration(
                  sessionInfo?.createdAt ||
                    details?.createdAt ||
                    details?.session?.createdAt,
                  sessionInfo?.endedAt ||
                    details?.endedAt ||
                    details?.session?.endedAt,
                )}
              </div>
            </div>
            <div className="card detail-notes">
              <div className="detail-notes-title">Notes</div>
              {!notesData?.notes?.length && (
                <div className="note-empty">
                  No notes yet.
                  <button
                    type="button"
                    className="btn btn-primary notes-add"
                    onClick={handleAddNote}
                  >
                    Add note
                  </button>
                </div>
              )}
              {notesData?.notes?.length > 0 && (
                <>
                  <div className="note-tabs">
                    {Array.from(
                      new Map(
                        notesData.notes.map((n) => [n.user?.publicId, n.user]),
                      ).values(),
                    ).map((user) => {
                      const isActive = activeNoteUser === user?.publicId;
                      return (
                        <button
                          key={user?.publicId}
                          type="button"
                          className={`note-tab${isActive ? " active" : ""}`}
                          onClick={() => setActiveNoteUser(user?.publicId)}
                        >
                          <img
                            src={
                              user?.profilePictureUrl || "/icons/Logo_PNG.png"
                            }
                            alt={user?.username || "User"}
                          />
                          <span>{user?.username || "User"}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="notes-list notes-compact">
                    <div
                      className="notes-list notes-compact"
                      onClick={openNotesModal}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          openNotesModal();
                        }
                      }}
                    >
                      {notesData.notes
                        .filter((n) => n.user?.publicId === activeNoteUser)
                        .slice(0, 2)
                        .map((note) => (
                          <NoteCard
                            key={note.id}
                            note={note}
                            canEdit={note.belongsToCurrentUser}
                            compact
                            onUpdate={handleUpdateNote}
                            onUploadImage={handleUploadImage}
                            onDelete={handleDeleteNote}
                            onDeleteImage={handleDeleteImage}
                            modalOnEdit
                          />
                        ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          <TeamEventsChart details={details} />
        </div>
      )}
      {showNotesModal && (
        <div className="note-modal-backdrop">
          <div className="note-modal" onClick={(e) => e.stopPropagation()}>
            <div className="note-modal-header">
              <div className="note-modal-title">All Notes</div>
              <button
                type="button"
                className="note-modal-close"
                onClick={() => setShowNotesModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="note-modal-body">
              {(notesData?.notes || []).map((note) => (
                <NoteCard
                  key={note.id}
                  note={note}
                  canEdit={note.belongsToCurrentUser}
                  onUpdate={handleUpdateNote}
                  onUploadImage={handleUploadImage}
                  onDelete={handleDeleteNote}
                  onDeleteImage={handleDeleteImage}
                  modalOnEdit
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamSessionDetailPage;
